import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

// MUI Icons
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import EditIcon from "@mui/icons-material/Edit";

// Components
import { EmissionFactorExcelModal } from "../modals/EmissionFactor/EmissionFactorExcelModal";

// API
import { emissionFactorAPI } from "../API/emissionFactorAPI";

// Utils
import { getConstByStandard } from "../utils/getConstFileByStandard";
import { getEASchemaByStandard } from "../modals/utils/getEASchemaByStandard";
import { getEFSchemaByStandard } from "../modals/utils/getEFSchemaByStandard";
import { flatToNested } from "../utils/ToNestEAFormData";
import { filterGWPValue } from "../utils/filterGWPValue";

// Stores
import emissionViewStore from "../../Store/marco/emissionViewStore";
import commonStore from "../../Store/marco/commonStore";

// Styles
import styles from "../styles/emissionFactor.module.css";

// ============================================================================
// Constants
// ============================================================================

/** Category codes that require special handling */
const CATEGORY_CODES = {
  CAT_4_3: "4_3",
  CAT_5_1: "5_1",
  CAT_1_4: "1_4",
};

/** Color for frozen/verifying state */
const VERIFYING_COLOR = "rgb(20, 100, 200)";

/** Number of emission factor columns to display in table */
const MAX_EF_COLUMNS = 3;

/** Label for GHG Emission Factor column that requires colspan */
const GHG_EMISSION_FACTOR_LABEL = "GHG Emission Factor";

/**
 * Mapping from GWP field keys to display labels
 */
export const GWP_LABEL_MAP = {
  co2GWPValue: "CO₂",
  ch4NoGWPValue: "CH₄",
  ch4FGWPValue: "CH₄",
  n2oGWPValue: "N₂O",
  hfcsGWPValue: "HFCs",
  pfcsGWPValue: "PFCs",
  sf6GWPValue: "SF₆",
  nf3GWPValue: "NF₃",
};

/**
 * Field mapping for Emission Activity (EA) data transformation
 */
const EA_FIELD_MAPPING = {
  isMassTransit: 10,
  fugitiveEmissions: 11,
  companyId: "selectedCom",
  id: "id",
};

/**
 * Field mapping for Emission Factor (EF) data transformation
 */
const EF_FIELD_MAPPING = {
  companyId: "selectedCom",
  id: "id",
};

/**
 * Fields to copy from EA to EF data for category 5_1
 */
const CAT_5_1_FIELDS_TO_COPY = [
  "energySupplier",
  "customEnergyName",
  "wasteManagementVendorName",
  "calculationId",
];

/**
 * Emission factor gas types and their corresponding field names
 */
const EMISSION_FACTOR_GASES = [
  { label: "CO₂", field: "co2EFValue" },
  { label: "CH₄", field: "ch4EFValue" },
  { label: "N₂O", field: "n2oEFValue" },
  { label: "HFCs", field: "hfcsEFValue" },
  { label: "PFCs", field: "pfcsEFValue" },
  { label: "SF₆", field: "sf6EFValue" },
  { label: "NF₃", field: "nf3EFValue" },
  { label: "CO₂e", field: "co2eEFValue" },
];

// ============================================================================
// Helper Functions
// ============================================================================

const shouldShowTitle = (titleArray, titleKey) => {
  return titleArray.includes(titleKey);
};

const mapEFDetailToFormData = (data) => ({
  isFrozen: data?.isFrozen,
  cId: data?.companyId,
  id: data?.id,
  name: data?.itemName,

  // Activity / EF shared fields
  activityNameId: data?.activityNameId ?? data?.activityId,
  emissionFactorTypeId: data?.emissionFactorTypeId ?? data?.emissionTypeId,

  sourceEFId: data?.sourceEFId,
  sourceEFName: data?.sourceEFName,

  emissionLevelId: data?.emissionLevelId,
  emissionLevel: data?.emissionLevel,

  emissionFactorId: data?.emissionFactorId,
  nameEF: data?.nameEF,
  unitCategoryId: data?.unitCategoryId,

  // Category 3.1 / 3.2 specific fields
  secondSourceEFId: data?.secondSourceEFId,
  secondSourceEFName: data?.secondSourceEFName,
  secondEmissionFactorId: data?.secondEFId,
  secondEmissionLevelId: data?.secondEmissionLevelId,
  secondEmissionLevel: data?.secondEmissionLevel,
  secondEmissionFactorTypeId: data?.secondEmissionTypeId,
  secondNameEF: data?.secondNameEF,
  shippingMethod: data?.modeId,

  // Category 4.3 Land specific fields
  landEFId: data?.landEFId,
  landEFTypeId: data?.landEFTypeId,
  landSourceEFId: data?.landSourceEFId,
  landSourceEFName: data?.landSourceEFName,
  landEmissionLevelId: data?.landEmissionLevelId,
  landEmissionLevel: data?.landEmissionLevel,
  landUnitCategoryId: data?.landUnitCategoryId,
  landNameEF: data?.landNameEF,
  haveTransport: data?.activityLandId,
  transportName: data?.activityLandName,
});

const processEAFormData = ({ EAData, EASchema, EFSchema, updateFunction }) => {
  const assignEA = (schemaDef, formKey) => {
    const nestedData = flatToNested(EAData, schemaDef, EA_FIELD_MAPPING);
    updateFunction(nestedData, formKey);
  };

  if (EFSchema.cat === CATEGORY_CODES.CAT_4_3) {
    assignEA(EASchema.normal);
    assignEA(EASchema.tran, "landEAFormData");
  } else {
    assignEA(EASchema);
  }
};

const processEFFormData = ({
  EAData,
  EFData,
  EFSchema,
  isSpecialCategory,
  updateFunction,
}) => {
  // Copy fields from EA to EF for category 5_1
  if (EFSchema.cat === CATEGORY_CODES.CAT_5_1) {
    CAT_5_1_FIELDS_TO_COPY.forEach((field) => {
      EFData[field] = EAData[field];
    });
  }

  const assignEF = (data, schemaDef, formKey) => {
    const nestedData = flatToNested(data, schemaDef, EF_FIELD_MAPPING);

    // Add otherEFNote if available
    if (EAData?.otherEFNote) {
      nestedData.otherEFNote = EAData.otherEFNote;
    }

    updateFunction(nestedData, formKey);
  };

  if (!isSpecialCategory) {
    // Standard category
    assignEF(EFData, EFSchema);
  } else if (EFSchema.cat === CATEGORY_CODES.CAT_4_3) {
    // Special land + transport category
    assignEF(EFData, EFSchema.normal);
    assignEF(EFData, EFSchema.tran, "EFLandFormData");
  }
};

const getEmissionFactorValues = (item) => {
  const values = EMISSION_FACTOR_GASES.map((gas) => ({
    label: gas.label,
    value: item[gas.field],
    unit: item.unit,
    emissionUnit: item.emissionUnit,
  })).filter(
    (gasData) =>
      gasData.value !== undefined &&
      gasData.value !== null &&
      gasData.value > 0,
  );

  // Pad with empty values to ensure exactly 3 columns
  while (values.length < MAX_EF_COLUMNS) {
    values.push({ label: "-", value: "-" });
  }

  return values;
};

const getUnitForColumn = (item, columnIndex, t) => {
  const unitMap = {
    0: item.firstUnit,
    1: item.secondUnit,
    2: item.thirdUnit,
  };
  return t(unitMap[columnIndex] || "");
};

// ============================================================================
// Component
// ============================================================================

export const EmissionFactor = observer(() => {
  // -------------------------------------------------------------------------
  // Stores & Hooks
  // -------------------------------------------------------------------------
  const store = emissionViewStore;
  const cmStore = commonStore;
  const { t } = useTranslation("ActivityMain");
  const { t: t1 } = useTranslation("EFMain");
  // -------------------------------------------------------------------------
  // Derived Values
  // -------------------------------------------------------------------------
  const CONST = getConstByStandard(store.standardId);
  const categoryMapping = CONST.category;
  const EASchema = getEASchemaByStandard(store.standardId)[store.selectedTab];
  const EFSchema = getEFSchemaByStandard(store.standardId)[store.selectedTab];

  // Get table configuration
  const tableConfig = CONST.tableTitles.find(
    (item) => item.label === store.selectedTab,
  );
  const tableTitles = tableConfig?.EFTitles || [];

  // Find current scope configuration
  const currentScopeConfig = cmStore?.EACategory?.find(
    (item) => item.label === store.selectedScope,
  );

  // Check if actions are disabled for current category
  const isActionDisabled = currentScopeConfig?.category?.find(
    (item) => item.label === store.selectedTab,
  )?.isDisabled;

  // Permission check
  const hasNoPermission = isActionDisabled || !cmStore.access;

  // Data state checks
  const isLoading = store.emissionActivityListLoading;
  const currentPageList = store.EFCurrentPageList;
  const isEmpty =
    !isLoading && (!currentPageList || currentPageList.length === 0);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Fetch emission factor list when dependencies change
  useEffect(() => {
    if (isActionDisabled) return;
    store.getEFListData({ keepPage: true });
  }, [
    store.selectedTab,
    store.currentCId,
    store.authComArr,
    store.selectedYear,
    store.isRefresh,
    store.searchKeyword,
    store.selectedScope,
  ]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleEditEmissionFactor = async (
    catName,
    categoryCode,
    id,
    categoryISO64,
  ) => {
    const {
      _handleSetEmissionActivityModalLoading,
      _handleEditEAFormData,
      _handleEditEFFormData,
    } = store;

    _handleSetEmissionActivityModalLoading(true);

    try {
      if (!catName || !categoryCode) return;

      const isSpecialCategory = [CATEGORY_CODES.CAT_4_3].includes(EFSchema.cat);

      // Fetch EA and EF data
      const EAData = await store.getSingleEAData(id, categoryISO64);
      const EFData = await store.getSingleEFData(id);

      // Process EA form data
      processEAFormData({
        EAData,
        EASchema,
        EFSchema,
        updateFunction: _handleEditEAFormData,
      });

      // Process EF form data
      processEFFormData({
        EAData,
        EFData,
        EFSchema,
        isSpecialCategory,
        updateFunction: _handleEditEFFormData,
      });
    } catch (err) {
      console.error("Edit emission factor error:", err);
    } finally {
      _handleSetEmissionActivityModalLoading(false);
    }
  };

  const handleSetEFFormData = async (item) => {
    try {
      const data = await emissionFactorAPI.getEmissionFactorDetail(
        item.id,
        categoryMapping[store.selectedTab],
      );

      const formData = mapEFDetailToFormData(data);
      store._handleSetEmissionFactorFormData(formData);
      store._handleSetIsShowEFIncertModal(true);
    } catch (error) {
      console.error("Failed to fetch emission factor details:", error);
    }
  };

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  /**
   * Renders entity name cell
   */
  const renderEntityNameCell = (item) => {
    if (!shouldShowTitle(tableTitles, "Entity Name") || !item.companyName) {
      return <td>-</td>;
    }

    return (
      <td>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
            paddingRight: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <span
              style={{
                fontSize: "15px",
                color: "#333",
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              {item.companyName}
            </span>
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#999",
              wordBreak: "break-word",
              textAlign: "left",
            }}
          >
            {t(item.hierarchyNamePath)}
          </div>
        </div>
      </td>
    );
  };

  /**
   * Renders activity name cell
   */
  const renderActivityNameCell = (item) => (
    <td>
      {shouldShowTitle(tableTitles, "Activity Name") &&
        (t(item.activityName) || "-")}
    </td>
  );

  /**
   * Renders EF database source cell
   */
  const renderEFSourceCell = (item) => {
    if (!item.sourceEFName) {
      return <td>-</td>;
    }

    return (
      <td>
        <div className={styles.efSourceCell}>
          <span>{t1(item.sourceEFName)}</span>
          {item.emissionFactorId ? (
            <span className={styles.efIdBadge}>{item.emissionFactorId}</span>
          ) : null}
        </div>
      </td>
    );
  };

  /**
   * Renders emission factor name cell
   */
  const renderEFNameCell = (item) => {
    if (item.isDirectEmission) {
      return <td>-</td>;
    }
    return <td>{t1(item.nameEF) || "-"}</td>;
  };

  /**
   * Renders emission factor value cells (3 columns)
   */
  const renderEFValueCells = (item) => {
    if (item.isDirectEmission) {
      return (
        <>
          <td>-</td>
          <td>-</td>
          <td>-</td>
        </>
      );
    }

    const efValues = getEmissionFactorValues(item);
    return efValues.map((efValue, index) => (
      <td key={index}>
        {efValue.value}
        <br />
        {getUnitForColumn(item, index, t)}
      </td>
    ));
  };

  /**
   * Renders GWP (Global Warming Potential) cell for category 1_4
   */
  const renderGWPCell = (item) => {
    if (EFSchema.cat !== CATEGORY_CODES.CAT_1_4) {
      return null;
    }

    const gwpData = filterGWPValue(item);
    const gwpEntries = Object.entries(gwpData);

    if (gwpEntries.length === 0) {
      return <td>-</td>;
    }

    return (
      <td>
        {gwpEntries.map(([key, value]) => (
          <div key={key} className={styles.dashedLine}>
            {GWP_LABEL_MAP[key] ?? key}: {value}
          </div>
        ))}
      </td>
    );
  };

  /**
   * Renders biomass indicator cell
   */
  const renderBiomassCell = (item) => {
    if (item.isDirectEmission) {
      return <td>-</td>;
    }

    if (item.isBiomass === false) {
      return <td>{t("No")}</td>;
    }

    if (item.isBiomass === true) {
      return <td>{t("Yes")}</td>;
    }

    return <td>-</td>;
  };

  /**
   * Renders action cell (Settings/Edit icons or status message)
   */
  const renderActionCell = (item) => {
    if (hasNoPermission) {
      return null;
    }

    if (item.isDirectEmission) {
      return <td>-</td>;
    }

    if (item.isFrozen) {
      return (
        <td style={{ color: VERIFYING_COLOR }}>{t("Verifying data...")}</td>
      );
    }

    return (
      <td>
        <SettingsOutlinedIcon
          sx={{ cursor: "pointer" }}
          onClick={async () => {
            try {
              handleEditEmissionFactor(
                EFSchema?.catName,
                EFSchema?.cat,
                item.id,
                item.categoryISO64,
              );
              store._handleEmissionFactorEditModal();
            } catch (error) {
              console.error("Failed to edit emission factor:", error);
            }
          }}
        />{" "}
        <EditIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleSetEFFormData(item)}
        />
      </td>
    );
  };

  /**
   * Renders table header
   */
  const renderTableHeader = () => (
    <thead>
      <tr>
        {tableTitles.map((title, index) => {
          let inlineStyle = { minWidth: "120px" };
          if (title === "No.") {
            inlineStyle = { width: "60px" };
          } else if (title === "Entity Name" || title === "Company Name") {
            inlineStyle = { width: "300px" };
          }
          return (
            <th
              {...(title === GHG_EMISSION_FACTOR_LABEL
                ? { colSpan: MAX_EF_COLUMNS }
                : { rowSpan: 2 })}
              key={index}
              style={inlineStyle}
            >
              {t(title)}
            </th>
          );
        })}

        {!hasNoPermission && (
          <th rowSpan="2" style={{ width: "100px" }}>
            {t("Actions")}
          </th>
        )}
      </tr>
      <tr>
        <td># 1</td>
        <td># 2</td>
        <td># 3</td>
      </tr>
    </thead>
  );

  /**
   * Renders table body
   */
  const renderTableBody = () => (
    <tbody>
      {currentPageList?.map((item, index) => {
        if (isActionDisabled) return null;

        return (
          <tr key={index}>
            <td>
              {(store.emissionFactorListData.page - 1) *
                store.emissionFactorListData.pageSize +
                index +
                1}
            </td>
            {renderEntityNameCell(item)}
            {renderActivityNameCell(item)}
            <td>{item.itemName || "-"}</td>
            {renderEFSourceCell(item)}
            {renderEFNameCell(item)}
            {renderEFValueCells(item)}
            {renderGWPCell(item)}
            {renderBiomassCell(item)}
            {renderActionCell(item)}
          </tr>
        );
      })}
    </tbody>
  );

  /**
   * Renders overlay messages (no data, not disclosed)
   */
  const renderOverlay = () => {
    if (isActionDisabled) {
      return (
        <div className={styles.noData}>
          <p>{t("This activity has not been disclosed")}</p>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className={styles.noData}>
          <p>{t("No data!")}</p>
        </div>
      );
    }

    return null;
  };

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        {renderTableHeader()}
        {renderTableBody()}
      </table>

      {store.isShowEmissionFactorExcelModal && <EmissionFactorExcelModal />}
      {renderOverlay()}
    </div>
  );
});
